"use client";

import React, { useState } from "react";
import NextLink from "next/link";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { formatCurrency } from "@configs/currencies";
import { useOrder } from "src/contexts";
import { useOrderQuery } from "src/hooks/useOrder";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import {
  useBundleCards,
  useOrderBundles,
  useSelectBundle,
} from "src/hooks/useBundles";
import { useBundlesEnabled } from "src/utils/bundles-enabled";
import type {
  BundleSlotProgressView,
  SelectedBundleView,
} from "src/services/bundle-api";
import {
  appliedHeaderCopy,
  bundlePanelModel,
  hintCopy,
  hintTitleCopy,
  nextLineCopy,
  orderBundleFields,
  panelHeaderCopy,
  slotHref,
  slotStatus,
  takenByCopy,
  type BundleBlock,
  type TakenBy,
} from "src/utils/bundles";

type Translate = ReturnType<typeof useAppTranslate>;

function SlotRows({
  slots,
  bundleId,
  locale,
  translate,
  compact = false,
  onNavigate,
}: {
  slots: BundleSlotProgressView[];
  bundleId: string;
  locale: string;
  translate: Translate;
  /** The drawer: the status goes under the label, which then has the width. */
  compact?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Stack>
      {slots.map((slot) => {
        const status = slotStatus(slot);
        return (
          <Box
            key={slot.slotId}
            component={NextLink}
            href={slotHref(slot, locale, bundleId)}
            data-testid={`bundle-slot-${slot.slotId}`}
            onClick={onNavigate}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 1,
              color: "inherit",
              textDecoration: "none",
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            {slot.full ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <Inventory2OutlinedIcon fontSize="small" color="action" />
            )}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: compact ? "column" : "row",
                alignItems: compact ? "flex-start" : "center",
                gap: compact ? 0 : 1.5,
              }}
            >
              <Typography variant="body2" sx={{ flex: compact ? undefined : 1 }}>
                {translate("bundles.slotLine", {
                  quantity: String(slot.need),
                  label: slot.label,
                })}
              </Typography>
              <Typography
                variant={compact ? "caption" : "body2"}
                color={slot.full ? "success.main" : "text.secondary"}
              >
                {translate(status.key, status.params)}
              </Typography>
            </Box>
            <ChevronRightIcon fontSize="small" color="action" />
          </Box>
        );
      })}
    </Stack>
  );
}

/**
 * APPLIED: a confirmation with the saving. The slot rows toward the next
 * application stay folded behind "Dopolni"; they never headline the block.
 */
function AppliedBlock({
  block,
  selected,
  showNext,
  fmt,
  compact,
  onNavigate,
  onChoose,
  choosing,
}: {
  block: Extract<BundleBlock, { state: "applied" }>;
  /** The view's selection when it is this bundle: its rows are the next one's. */
  selected: SelectedBundleView | null;
  showNext: boolean;
  fmt: (cents: number) => string;
  compact: boolean;
  onNavigate?: () => void;
  /** Selects this bundle so its rows exist; resolves false on failure. */
  onChoose: () => Promise<boolean>;
  choosing: boolean;
}) {
  const locale = useLocale();
  const translate = useAppTranslate();
  const [open, setOpen] = useState(false);
  const header = appliedHeaderCopy(block);
  const next = showNext ? block.next : null;
  const nextCopy = next
    ? nextLineCopy(next, fmt, translate("bundles.hintJoin"))
    : null;
  const toggle = async () => {
    if (open) return setOpen(false);
    // An auto-matched bundle has no rows until it is the selected one.
    if (!selected && !(await onChoose())) return;
    setOpen(true);
  };
  return (
    <Paper
      variant="outlined"
      data-testid={`bundle-applied-${block.bundleId}`}
      sx={{
        p: compact ? 1.5 : 2,
        mb: 2,
        borderRadius: 2,
        borderColor: "success.light",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CheckCircleIcon color="success" fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          {translate(header.key, header.params)}
        </Typography>
        <Typography
          variant="subtitle1"
          color="success.main"
          sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
        >{`−${fmt(block.discountCents)}`}</Typography>
      </Box>
      {block.contents.length > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="bundle-applied-contents"
          sx={{ pl: 3.5 }}
        >
          {block.contents
            .map((c) =>
              translate("bundles.slotLine", {
                quantity: String(c.quantity),
                label: c.label,
              }),
            )
            .join(" · ")}
        </Typography>
      )}
      {next && nextCopy && (
        <>
          <Box
            data-testid="bundle-next"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pl: 3.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              {translate(nextCopy.key, nextCopy.params)}
            </Typography>
            <Button
              size="small"
              data-testid="bundle-next-action"
              aria-expanded={open}
              disabled={choosing}
              endIcon={open ? <ExpandLessIcon /> : <ChevronRightIcon />}
              onClick={() => void toggle()}
              sx={{ flexShrink: 0 }}
            >
              {translate("bundles.nextAction")}
            </Button>
          </Box>
          <Collapse in={open && !!selected} unmountOnExit>
            {selected && (
              <Box sx={{ pl: 3.5, mt: 0.5 }}>
                <SlotRows
                  slots={selected.slots}
                  bundleId={block.bundleId}
                  locale={locale}
                  translate={translate}
                  compact={compact}
                  onNavigate={onNavigate}
                />
              </Box>
            )}
          </Collapse>
        </>
      )}
    </Paper>
  );
}

/**
 * The paket block, the same wherever the cart shows (drawer, home page,
 * checkout): one block per bundle, FILLING (header, slot rows, "Odstrani
 * paket") until it applies, then APPLIED (a confirmation with the saving and
 * at most one quiet line toward the next one). See bundlePanelModel. Only UI:
 * the price follows the cart either way.
 *
 * The applied blocks come from the order itself (`appliedBundles`, written
 * with `amount`), so they show whenever the total includes them, the switch
 * off or the `/bundles` request failed included.
 *
 * Renders nothing at all, `wrapper` included, when there is nothing to show.
 */
export function BundlePanel({
  order,
  guestToken,
  amountKey,
  compact = false,
  onNavigate,
  showHint = false,
  suppressBundleHints = false,
  confirmationOnly = false,
  wrapper: Wrapper,
}: {
  order: { id?: string | null; currency?: string | null } | null | undefined;
  guestToken: string | null | undefined;
  amountKey: unknown;
  compact?: boolean;
  /** Called as a link is followed; the cart drawer closes itself with it. */
  onNavigate?: () => void;
  /** The server's hint for a bundle with no block, as a card of its own. */
  showHint?: boolean;
  /** The ladder hint won (one hint at a time): no next-paket line, no hint card. */
  suppressBundleHints?: boolean;
  /**
   * Checkout steps: only the APPLIED confirmations (header, saving,
   * contents). No FILLING block, no next line, no hint: nothing that pulls
   * the customer away from paying.
   */
  confirmationOnly?: boolean;
  wrapper?: React.ComponentType<{ children?: React.ReactNode }>;
}) {
  const locale = useLocale();
  const translate = useAppTranslate();
  const router = useRouter();
  const enabled = useBundlesEnabled();
  const select = useSelectBundle();
  const [error, setError] = useState<string | null>(null);
  const orderId = order?.id ?? null;
  const { data: view } = useOrderBundles(
    orderId,
    guestToken,
    amountKey,
    locale,
  );
  const currency = order?.currency || view?.currency || "eur";
  const { data: cards } = useBundleCards(currency, locale);
  if (!orderId) return null;

  const live = enabled ? (view ?? null) : null;
  const full = bundlePanelModel({
    applied: orderBundleFields(order).appliedBundles,
    view: live,
    cards,
    locale,
  });
  const model = confirmationOnly
    ? {
        ...full,
        blocks: full.blocks.filter((b) => b.state === "applied"),
        hint: null,
      }
    : full;
  const hideHints = suppressBundleHints || confirmationOnly;
  const hintView = showHint && !hideHints ? model.hint : null;
  if (model.blocks.length === 0 && !hintView) return null;

  const fmt = (cents: number) => formatCurrency(cents / 100, currency);
  const selected = live?.selected ?? null;
  const choose = async (bundleId: string) => {
    setError(null);
    try {
      await select.mutateAsync({ orderId, guestToken, bundleId, locale });
      return true;
    } catch {
      setError(translate("bundles.chooseError"));
      return false;
    }
  };
  const openSlots = async (bundleId: string) => {
    if (!(await choose(bundleId))) return;
    onNavigate?.();
    // `bundle` marks in-session paket navigation: the home page then offers
    // no "unfinished order" resume (order/new).
    router.push(`/${locale}?bundle=${encodeURIComponent(bundleId)}`);
  };
  const hint = hintView
    ? hintCopy(hintView, fmt, translate("bundles.hintJoin"), cards)
    : null;
  const hintTitle = hintView ? hintTitleCopy(hintView, cards, fmt) : null;

  const body = (
    <Box data-testid="bundle-slots-panel">
      {model.blocks.map((block) =>
        block.state === "applied" ? (
          <AppliedBlock
            key={block.bundleId}
            block={block}
            selected={selected?.bundleId === block.bundleId ? selected : null}
            showNext={!hideHints}
            fmt={fmt}
            compact={compact}
            onNavigate={onNavigate}
            onChoose={() => choose(block.bundleId)}
            choosing={select.isPending}
          />
        ) : (
          <FillingBlock
            key={block.bundleId}
            selected={block.selected}
            takenBy={block.takenBy}
            fmt={fmt}
            compact={compact}
            onNavigate={onNavigate}
            removing={select.isPending}
            onRemove={() =>
              select.mutate({ orderId, guestToken, bundleId: null, locale })
            }
          />
        ),
      )}
      {hintView && hint ? (
        <Paper
          data-testid="bundle-hint"
          variant="outlined"
          sx={{ p: 1.5, mb: 2, borderRadius: 2 }}
        >
          {hintTitle && (
            <Typography
              variant="subtitle2"
              data-testid="bundle-hint-title"
              sx={{ fontWeight: 700 }}
            >
              {translate(hintTitle.key, hintTitle.params)}
            </Typography>
          )}
          <Typography variant="body2">
            {translate(hint.key, hint.params)}
          </Typography>
          <Button
            data-testid="bundle-hint-action"
            size="small"
            sx={{ mt: 0.5 }}
            disabled={select.isPending}
            onClick={() => void openSlots(hintView.bundleId)}
          >
            {translate("bundles.hintAdd")}
          </Button>
        </Paper>
      ) : null}
      {error ? (
        <Typography
          data-testid="bundle-hint-error"
          variant="body2"
          color="error"
          sx={{ mb: 1 }}
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
  return Wrapper ? <Wrapper>{body}</Wrapper> : body;
}

/** FILLING: the selected bundle before it applies, one row per slot. */
function FillingBlock({
  selected,
  takenBy,
  fmt,
  compact,
  onNavigate,
  removing,
  onRemove,
}: {
  selected: SelectedBundleView;
  /** Another applied paket took this one's items: say so (deep walk N3). */
  takenBy: TakenBy | null;
  fmt: (cents: number) => string;
  compact: boolean;
  onNavigate?: () => void;
  removing: boolean;
  onRemove: () => void;
}) {
  const locale = useLocale();
  const translate = useAppTranslate();
  const header = panelHeaderCopy(selected, fmt);
  const taken = takenBy ? takenByCopy(takenBy) : null;
  return (
    <Paper
      variant="outlined"
      data-testid="bundle-filling"
      sx={{ p: compact ? 1.5 : 2, mb: 2, borderRadius: 2 }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {translate(header.key, header.params)}
      </Typography>
      {taken && (
        <Box
          data-testid="bundle-taken-by"
          sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}
        >
          <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: "2px" }} />
          <Typography variant="body2" color="text.secondary">
            {translate(taken.key, taken.params)}
          </Typography>
        </Box>
      )}
      <SlotRows
        slots={selected.slots}
        bundleId={selected.bundleId}
        locale={locale}
        translate={translate}
        compact={compact}
        onNavigate={onNavigate}
      />
      <Button
        size="small"
        sx={{ mt: 1 }}
        data-testid="bundle-remove"
        disabled={removing}
        onClick={onRemove}
      >
        {translate("bundles.remove")}
      </Button>
    </Paper>
  );
}

/** The paket block for the active cart (the home page and the cart drawer). */
export default function BundleSlotsPanel({
  compact = false,
  onNavigate,
  showHint = false,
  suppressBundleHints = false,
}: {
  compact?: boolean;
  onNavigate?: () => void;
  showHint?: boolean;
  suppressBundleHints?: boolean;
}) {
  const { mainOrderId, guestToken } = useOrder();
  const { data: order } = useOrderQuery(mainOrderId, guestToken);
  if (!mainOrderId) return null;
  return (
    <BundlePanel
      order={{ ...order, id: mainOrderId }}
      guestToken={guestToken}
      amountKey={order?.amount}
      compact={compact}
      onNavigate={onNavigate}
      showHint={showHint}
      suppressBundleHints={suppressBundleHints}
    />
  );
}
